/** TanStack Query hooks for background jobs (generation, personal lessons). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { BackgroundJob } from '@formency/shared';

// Re-export for convenience
export type { BackgroundJob } from '@formency/shared';

// ─── Query key factory ─────────────────────────────────────────────────────

export const jobKeys = {
  all: ['f', 'jobs'] as const,
  list: (params?: { type?: string; status?: string }) => [...jobKeys.all, 'list', params] as const,
  detail: (jobId: string) => [...jobKeys.all, jobId] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** List the current user's background jobs, with optional type/status filter and polling. */
export function useMyJobs(
  params?: { type?: string; status?: string },
  options?: {
    refetchInterval?:
      | number
      | false
      | ((query: { state: { data: BackgroundJob[] | undefined } }) => number | false);
    enabled?: boolean;
  }
) {
  return useQuery<BackgroundJob[]>({
    queryKey: jobKeys.list(params),
    queryFn: () =>
      api.get<BackgroundJob[]>('/me/jobs', params as Record<string, unknown> | undefined),
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Poll a single background job by ID.
 * Pass `refetchInterval` to control polling frequency (e.g. 2000 for 2s).
 * Set `enabled: false` to pause polling.
 */
export function useJob(
  jobId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useQuery<BackgroundJob>({
    queryKey: jobKeys.detail(jobId!),
    queryFn: () => api.get<BackgroundJob>(`/me/jobs/${jobId}`),
    enabled: (options?.enabled ?? true) && !!jobId,
    refetchInterval: options?.refetchInterval,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

/** Mark a terminal job as acknowledged (hides it from the sidebar). */
export function useAcknowledgeJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      api.patch<{ acknowledged: boolean }>(`/me/jobs/${jobId}/acknowledge`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}
